'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── FEEDING LOGS ────────────────────────────────────────────────────────────

export interface FeedingLog {
  id: string;
  userId: string;
  bedId: string;
  feedType: string;
  feedQuantityKg: number;
  beddingMaterial?: string;
  moistureLevel?: string;
  phReading?: number;
  feedingDate: string;
  notes?: string;
  createdAt: string;
  loggedBy?: string;
}

function mapFeedingLog(row: any): FeedingLog {
  return {
    id: row.id,
    userId: row.user_id,
    bedId: row.bed_id,
    feedType: row.feed_type,
    feedQuantityKg: Number(row.feed_quantity_kg),
    beddingMaterial: row.bedding_material ?? undefined,
    moistureLevel: row.moisture_level ?? undefined,
    phReading: row.ph_reading != null ? Number(row.ph_reading) : undefined,
    feedingDate: row.feeding_date,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
    loggedBy: row.user_profiles?.full_name ?? undefined,
  };
}

export const feedingService = {
  async getAll(): Promise<FeedingLog[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('feeding_logs')
        .select('*, user_profiles(full_name)')
        .order('feeding_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapFeedingLog);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    userId: string;
    bedId: string;
    feedType: string;
    feedQuantityKg: number;
    beddingMaterial?: string;
    moistureLevel?: string;
    phReading?: number;
    feedingDate: string;
    notes?: string;
  }): Promise<FeedingLog | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('feeding_logs')
        .insert({
          user_id: payload.userId,
          bed_id: payload.bedId,
          feed_type: payload.feedType,
          feed_quantity_kg: payload.feedQuantityKg,
          bedding_material: payload.beddingMaterial || null,
          moisture_level: payload.moistureLevel || null,
          ph_reading: payload.phReading ?? null,
          feeding_date: payload.feedingDate,
          notes: payload.notes || null,
        })
        .select('*, user_profiles(full_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapFeedingLog(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('feeding_logs').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── HARVEST RECORDS ─────────────────────────────────────────────────────────

export interface HarvestRecord {
  id: string;
  userId: string;
  bedId: string;
  harvestDate: string;
  wetWeightKg: number;
  dryWeightKg?: number;
  volumeL?: number;
  qualityGrade: string;
  moisturePct?: number;
  batchNotes?: string;
  createdAt: string;
  loggedBy?: string;
}

function mapHarvestRecord(row: any): HarvestRecord {
  return {
    id: row.id,
    userId: row.user_id,
    bedId: row.bed_id,
    harvestDate: row.harvest_date,
    wetWeightKg: Number(row.wet_weight_kg),
    dryWeightKg: row.dry_weight_kg != null ? Number(row.dry_weight_kg) : undefined,
    volumeL: row.volume_l != null ? Number(row.volume_l) : undefined,
    qualityGrade: row.quality_grade,
    moisturePct: row.moisture_pct != null ? Number(row.moisture_pct) : undefined,
    batchNotes: row.batch_notes ?? undefined,
    createdAt: row.created_at,
    loggedBy: row.user_profiles?.full_name ?? undefined,
  };
}

export const harvestService = {
  async getAll(): Promise<HarvestRecord[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('harvest_records')
        .select('*, user_profiles(full_name)')
        .order('harvest_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapHarvestRecord);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    userId: string;
    bedId: string;
    harvestDate: string;
    wetWeightKg: number;
    dryWeightKg?: number;
    volumeL?: number;
    qualityGrade: string;
    moisturePct?: number;
    batchNotes?: string;
  }): Promise<HarvestRecord | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('harvest_records')
        .insert({
          user_id: payload.userId,
          bed_id: payload.bedId,
          harvest_date: payload.harvestDate,
          wet_weight_kg: payload.wetWeightKg,
          dry_weight_kg: payload.dryWeightKg ?? null,
          volume_l: payload.volumeL ?? null,
          quality_grade: payload.qualityGrade,
          moisture_pct: payload.moisturePct ?? null,
          batch_notes: payload.batchNotes || null,
        })
        .select('*, user_profiles(full_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapHarvestRecord(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('harvest_records').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── POPULATION CHECKS ───────────────────────────────────────────────────────

export interface PopulationCheck {
  id: string;
  userId: string;
  bedId: string;
  checkDate: string;
  healthScore: number;
  adultCount?: number;
  juvenileCount?: number;
  cocoonCount?: number;
  temperatureC?: number;
  moisturePct?: number;
  observations?: string[];
  notes?: string;
  createdAt: string;
  loggedBy?: string;
}

function mapPopulationCheck(row: any): PopulationCheck {
  return {
    id: row.id,
    userId: row.user_id,
    bedId: row.bed_id,
    checkDate: row.check_date,
    healthScore: Number(row.health_score),
    adultCount: row.adult_count ?? undefined,
    juvenileCount: row.juvenile_count ?? undefined,
    cocoonCount: row.cocoon_count ?? undefined,
    temperatureC: row.temperature_c != null ? Number(row.temperature_c) : undefined,
    moisturePct: row.moisture_pct != null ? Number(row.moisture_pct) : undefined,
    observations: row.observations ?? undefined,
    notes: row.notes ?? undefined,
    createdAt: row.created_at,
    loggedBy: row.user_profiles?.full_name ?? undefined,
  };
}

export const populationService = {
  async getAll(): Promise<PopulationCheck[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('population_checks')
        .select('*, user_profiles(full_name)')
        .order('check_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapPopulationCheck);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    userId: string;
    bedId: string;
    checkDate: string;
    healthScore: number;
    adultCount?: number;
    juvenileCount?: number;
    cocoonCount?: number;
    temperatureC?: number;
    moisturePct?: number;
    observations?: string[];
    notes?: string;
  }): Promise<PopulationCheck | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('population_checks')
        .insert({
          user_id: payload.userId,
          bed_id: payload.bedId,
          check_date: payload.checkDate,
          health_score: payload.healthScore,
          adult_count: payload.adultCount ?? null,
          juvenile_count: payload.juvenileCount ?? null,
          cocoon_count: payload.cocoonCount ?? null,
          temperature_c: payload.temperatureC ?? null,
          moisture_pct: payload.moisturePct ?? null,
          observations: payload.observations?.length ? payload.observations : null,
          notes: payload.notes || null,
        })
        .select('*, user_profiles(full_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapPopulationCheck(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('population_checks').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── WORM BEDS ────────────────────────────────────────────────────────────────

export interface WormBed {
  id: string;
  bedName: string;
  capacityKg: number;
  assignedWorkerId?: string;
  assignedWorkerName?: string;
  isActive: boolean;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function mapWormBed(row: any): WormBed {
  return {
    id: row.id,
    bedName: row.bed_name,
    capacityKg: Number(row.capacity_kg),
    assignedWorkerId: row.assigned_worker_id ?? undefined,
    assignedWorkerName: row.worker?.full_name ?? undefined,
    isActive: row.is_active,
    notes: row.notes ?? undefined,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const wormBedService = {
  async getAll(): Promise<WormBed[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('worm_beds')
        .select('*, worker:assigned_worker_id(full_name)')
        .order('bed_name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapWormBed);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    bedName: string;
    capacityKg: number;
    assignedWorkerId?: string;
    isActive: boolean;
    notes?: string;
    createdBy: string;
  }): Promise<WormBed | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('worm_beds')
        .insert({
          bed_name: payload.bedName,
          capacity_kg: payload.capacityKg,
          assigned_worker_id: payload.assignedWorkerId || null,
          is_active: payload.isActive,
          notes: payload.notes || null,
          created_by: payload.createdBy,
        })
        .select('*, worker:assigned_worker_id(full_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapWormBed(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async update(
    id: string,
    payload: {
      bedName: string;
      capacityKg: number;
      assignedWorkerId?: string;
      isActive: boolean;
      notes?: string;
    }
  ): Promise<WormBed | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('worm_beds')
        .update({
          bed_name: payload.bedName,
          capacity_kg: payload.capacityKg,
          assigned_worker_id: payload.assignedWorkerId || null,
          is_active: payload.isActive,
          notes: payload.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*, worker:assigned_worker_id(full_name)')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapWormBed(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('worm_beds').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },

  async toggleActive(id: string, isActive: boolean): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('worm_beds')
        .update({ is_active: isActive, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};

// ─── WORKERS (user_profiles list for assignment) ──────────────────────────────

export interface WorkerProfile {
  id: string;
  fullName: string;
  farmRole: string;
}

export const workerService = {
  async getAll(): Promise<WorkerProfile[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, full_name, farm_role')
        .eq('is_active', true)
        .order('full_name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map((row: any) => ({
        id: row.id,
        fullName: row.full_name,
        farmRole: row.farm_role,
      }));
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },
};

// ─── FARM WORKERS ─────────────────────────────────────────────────────────────

export interface FarmWorker {
  id: string;
  fullName: string;
  role: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  notes?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

function mapFarmWorker(row: any): FarmWorker {
  return {
    id: row.id,
    fullName: row.full_name,
    role: row.role,
    phone: row.phone ?? undefined,
    email: row.email ?? undefined,
    isActive: row.is_active,
    notes: row.notes ?? undefined,
    createdBy: row.created_by ?? undefined,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

export const farmWorkerService = {
  async getAll(): Promise<FarmWorker[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('farm_workers')
        .select('*')
        .order('full_name', { ascending: true });
      if (error) {
        if (isSchemaError(error)) throw error;
        return [];
      }
      return (data ?? []).map(mapFarmWorker);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return [];
    }
  },

  async create(payload: {
    fullName: string;
    role: string;
    phone?: string;
    email?: string;
    isActive: boolean;
    notes?: string;
    createdBy: string;
  }): Promise<FarmWorker | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('farm_workers')
        .insert({
          full_name: payload.fullName,
          role: payload.role,
          phone: payload.phone || null,
          email: payload.email || null,
          is_active: payload.isActive,
          notes: payload.notes || null,
          created_by: payload.createdBy,
        })
        .select('*')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapFarmWorker(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async update(
    id: string,
    payload: {
      fullName: string;
      role: string;
      phone?: string;
      email?: string;
      isActive: boolean;
      notes?: string;
    }
  ): Promise<FarmWorker | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('farm_workers')
        .update({
          full_name: payload.fullName,
          role: payload.role,
          phone: payload.phone || null,
          email: payload.email || null,
          is_active: payload.isActive,
          notes: payload.notes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select('*')
        .single();
      if (error) {
        if (isSchemaError(error)) throw error;
        return null;
      }
      return mapFarmWorker(data);
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return null;
    }
  },

  async delete(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('farm_workers').delete().eq('id', id);
      if (error) {
        if (isSchemaError(error)) throw error;
        return false;
      }
      return true;
    } catch (e: any) {
      if (isSchemaError(e)) throw e;
      return false;
    }
  },
};
